import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@stores/store';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import useUserById from '@hooks/api/useUserById';
import useUserModulesById from '@hooks/api/useUserModulesById';
import useUpdateUser from '@hooks/api/useUpdateUser';
import { DashboardSelectorSubmoduleItem } from '../types/dashboardSelector';
import { UserRole } from '../types/userDetailsForm';
import { buildListableDashboardModules } from '../utils/utils';
import { parseInitialUserModuleAccess } from '../utils/parseInitialUserModuleAccess';
import { buildSubmodulePayload } from '../utils/utils';
import { EditUserData } from '../types/userDetailsForm';
import { CUSTOM_TEMPLATE } from '../constants';

interface HandleUpdateParams {
  userId: string;
  email: string;
  username: string;
  userRole: string;
  companyId: string;
  internalUserAccessTemplateType: string;
  selectedSubmodules: DashboardSelectorSubmoduleItem[];
}

type UseEditUserResult = {
  userData: EditUserData | null;
  initialSubmodules: DashboardSelectorSubmoduleItem[] | undefined;
  isLoading: boolean;
  isSaving: boolean;
  handleUpdate: (params: HandleUpdateParams) => Promise<boolean>;
};

export const useEditUser = (userId: string): UseEditUserResult => {
  const dispatch = useDispatch();
  const { getUserDetailsById, isLoading: isLoadingUser } = useUserById();
  const { getUserModuleDetailsById, isLoading: isLoadingModules } = useUserModulesById();
  const { editUser, isLoading: isSaving } = useUpdateUser();

  const modulesState = useSelector((state: RootState) => state.ModulesData);
  const catalog = useMemo(
    () => buildListableDashboardModules(modulesState.userManagementModules, true),
    [modulesState.userManagementModules],
  );

  const [userData, setUserData] = useState<EditUserData | null>(null);
  const [initialSubmodules, setInitialSubmodules] = useState<
    DashboardSelectorSubmoduleItem[] | undefined
  >(undefined);

  useEffect(() => {
    if (!userId) return;

    const fetchUserDetails = async () => {
      const user = await getUserDetailsById(userId);
      if (user) {
        setUserData({
          email: user.email ?? '',
          username: user.username ?? '',
          userRole: (user.role ?? 'VIEWER') as UserRole,
          companyId: user.companyId ?? '',
          internalUserAccessTemplateType: user.internalUserAccessTemplateType,
        });
      }
    };

    fetchUserDetails();
  }, [userId]);

  useEffect(() => {
    if (!userId || modulesState.loadCount === 0 || catalog.length === 0) return;

    const fetchModuleAccess = async () => {
      const response = await getUserModuleDetailsById(userId);
      if (!response?.data) {
        setInitialSubmodules([]);
        return;
      }
      setInitialSubmodules(parseInitialUserModuleAccess(response.data, catalog));
    };

    fetchModuleAccess();
  }, [userId, modulesState.loadCount, catalog]);

  const handleUpdate = async ({
    userId: id,
    email,
    username,
    userRole,
    companyId,
    internalUserAccessTemplateType,
    selectedSubmodules,
  }: HandleUpdateParams): Promise<boolean> => {
    const isAdmin = userRole === 'ADMIN';
    const res = await editUser({
      id,
      email,
      username,
      userRole,
      companyId,
      submoduleList: isAdmin ? [] : buildSubmodulePayload(selectedSubmodules),
      ...(!isAdmin &&
        internalUserAccessTemplateType !== CUSTOM_TEMPLATE.internalUserAccessTemplateType && {
          internalUserAccessTemplateType,
        }),
    });

    const success = Boolean((res as any)?.code);
    dispatch(
      setToastMessage({
        type: success ? TOAST_TYPES.SUCCESS : TOAST_TYPES.ERROR,
        message: success
          ? `${email} updated!`
          : ((res as any)?.error?.message ?? `Failed to update ${email}`),
        heading: success ? '' : `Failed to update ${email}, please try again !`,
      }),
    );

    return success;
  };

  return {
    userData,
    initialSubmodules,
    isLoading: isLoadingUser || isLoadingModules,
    isSaving,
    handleUpdate,
  };
};
